/**
 * An Area's page (slice 6d spec §8.2; Paper A1, A1b): its icon and name,
 * Where it stands, an ask that starts a chat in it, then Needs someone,
 * Tasks and Chats. On a desktop Docs, Routines and This week are in a side
 * column; on a phone Tasks and Chats are tabs, with Docs and Routines below.
 * What the crew knows heads the side column, folded; For this
 * area and the email address join it in later PRs.
 */
import { Button } from "@crew/ui/components/button";
import { Item, ItemContent, ItemMedia } from "@crew/ui/components/item";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { ThreadTile } from "@crew/ui/components/crew-side-panel";
import { Tabs, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { Eyebrow, TextLink } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { ArrowDownToLine, Bell, ChevronDown, Pencil, Plus, Repeat } from "lucide-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint } from "../../app/tints";
import { ChatIcon } from "@crew/ui/components/chat-icon";
import type { ThreadItem } from "../chats/api";
import { ActivityBadge } from "../chats/chats-page";
import { shortTime } from "../chats/format";
import { tileLetter, tileStatus, tileTint } from "../chats/rail";
import { StartComposer } from "../chats/start-composer";
import { DocIcon } from "../docs/parts";
import { usePeople } from "../people/api";
import { whenLabel } from "../routines/routine-view";
import type { RoutineItem } from "../routines/api";
import { NewTaskWindow } from "../tasks/new-task";
import { TaskStatus } from "../tasks/parts";
import type { TaskItem } from "../tasks/api";
import { useAreaPage, useEditArea, type AreaPage as AreaPageData } from "./api";
import { EditAreaWindow } from "./area-form";
import { areaMoves, authorName, weekLine } from "./area-view";
import { briefHref, CARD_FOLD, cardBrief } from "./brief";
import { MentionText } from "./mention-parts";
import { AreaApps } from "./area-apps";
import { MoveInWindow } from "./move-in";
import { AreaTile } from "./parts";

/** Who wrote Where it stands, the people read only when a person did. */
function useAuthor(by: string | null, me: Me): string | null {
  const people = usePeople({ enabled: Boolean(by?.startsWith("person:")) });
  return authorName(by, me, people.data?.people);
}

function Section({
  title,
  count,
  aside,
  children,
  ...props
}: {
  title: string;
  count?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  [data: `data-${string}`]: string | boolean;
}) {
  return (
    <section className="flex flex-col gap-3" {...props}>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-lead font-semibold text-foreground">
          {title}
          {count !== undefined ? (
            <span className="ml-2 text-meta font-normal text-muted-foreground tabular">
              {count}
            </span>
          ) : null}
        </h2>
        {aside ? <div className="shrink-0 text-meta">{aside}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** A white card of rows, one hairline between each. */
function Rows({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <ul
      className={cn(
        "flex flex-col divide-y divide-border rounded-xl bg-card ring-1 ring-border",
        className,
      )}
    >
      {children}
    </ul>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed border-border px-5 py-6 text-center text-body text-muted-foreground">
      {children}
    </p>
  );
}

function TaskRows({ tasks, me }: { tasks: TaskItem[]; me: Me }) {
  return (
    <Rows>
      {tasks.map((task) => (
        <li key={task.id} data-task-id={task.id}>
          <Link
            href={`/tasks/${task.id}`}
            className="flex flex-col gap-1 px-4 py-3.5 md:flex-row md:items-center md:gap-4 md:px-5"
          >
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-body font-medium text-foreground">{task.title}</span>
              {task.detail ? (
                <span className="truncate text-meta text-muted-foreground">{task.detail}</span>
              ) : null}
            </span>
            <span className="flex items-center justify-between gap-3 md:contents">
              <TaskStatus task={task} className="min-w-0 md:w-[170px] md:shrink-0" />
              <span className="shrink-0 text-meta text-muted-foreground md:w-[56px] md:text-right">
                {shortTime(task.updated_at, me.workspace.timezone)}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </Rows>
  );
}

function ChatRows({ chats, me }: { chats: ThreadItem[]; me: Me }) {
  return (
    <Rows>
      {chats.map((chat) => (
        <li key={chat.id} data-thread-id={chat.id}>
          <Item
            render={<Link href={`/chats/${chat.id}`} />}
            className="flex-nowrap items-center gap-3 px-4 py-3 md:px-5"
          >
            <ItemMedia>
              <ThreadTile
                letter={tileLetter(chat.title)}
                tint={tileTint(chat.id)}
                status={tileStatus(chat.activity)}
                icon={chat.icon}
                imessage={chat.messaging_app === "imessage"}
                agent={
                  chat.agent
                    ? {
                        name: chat.agent.name,
                        glyph: chat.agent.glyph,
                        tint: agentTint(chat.agent.tint),
                      }
                    : null
                }
              />
            </ItemMedia>
            <ItemContent className="min-w-0 gap-0.5">
              <span className="truncate text-body font-medium text-foreground">{chat.title}</span>
              {chat.preview ? (
                <span className="truncate text-meta text-muted-foreground">{chat.preview}</span>
              ) : null}
            </ItemContent>
            <span className="flex shrink-0 flex-col items-end gap-1">
              <span className="text-meta text-muted-foreground">
                {shortTime(chat.last_message_at, me.workspace.timezone)}
              </span>
              <ActivityBadge activity={chat.activity} />
            </span>
          </Item>
        </li>
      ))}
    </Rows>
  );
}

/** A side card: an eyebrow, its count, and All. */
function SideCard({
  title,
  count,
  href,
  children,
  ...props
}: {
  title: string;
  count?: number;
  href?: string;
  children: ReactNode;
  [data: `data-${string}`]: string | boolean;
}) {
  return (
    <section
      className="flex flex-col gap-3 rounded-xl bg-card px-5 py-4 ring-1 ring-border"
      {...props}
    >
      <div className="flex items-baseline justify-between">
        <Eyebrow>
          {title}
          {count ? <span className="ml-2 font-normal tabular">{count}</span> : null}
        </Eyebrow>
        {href ? <TextLink href={href}>All</TextLink> : null}
      </div>
      {children}
    </section>
  );
}

function DocList({ page, me }: { page: AreaPageData; me: Me }) {
  if (page.docs.length === 0) {
    return (
      <p className="text-meta text-muted-foreground">No docs yet. The crew writes them here.</p>
    );
  }
  return (
    <ul className="flex flex-col divide-y divide-border">
      {page.docs.map((doc) => (
        <li key={doc.id} data-doc-id={doc.id}>
          <Link href={`/docs/${doc.id}`} className="flex items-start gap-3 py-2.5">
            <DocIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-body text-foreground">{doc.title}</span>
              <span className="text-meta text-muted-foreground">
                {doc.updated_by.name} · {shortTime(doc.updated_at, me.workspace.timezone)}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function RoutineList({ routines, me }: { routines: RoutineItem[]; me: Me }) {
  if (routines.length === 0) {
    return <p className="text-meta text-muted-foreground">No routines here yet.</p>;
  }
  const now = new Date();
  return (
    <ul className="flex flex-col divide-y divide-border">
      {routines.map((r) => {
        const Icon = r.kind === "once" ? Bell : Repeat;
        return (
          <li key={r.id} data-routine-id={r.id}>
            <Link href={`/routines/${r.id}`} className="flex items-start gap-3 py-2.5">
              <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-body text-foreground">{r.title}</span>
                <span className="text-meta text-muted-foreground">
                  {whenLabel(r, me.workspace.timezone, now)}
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function WeekLine({ week, slug }: { week: NonNullable<AreaPageData["week"]>; slug: string }) {
  return (
    <p className="px-1 text-meta text-muted-foreground" data-area-week>
      {weekLine(week)} <TextLink href={`/activity?area=${slug}`}>See it in Activity</TextLink>
    </p>
  );
}

function Header({ page, me, onEdit }: { page: AreaPageData; me: Me; onEdit: () => void }) {
  const { area } = page;
  const author = useAuthor(area.summary_by, me);
  return (
    <header className="flex flex-col gap-3">
      <div className="flex items-center gap-3 md:gap-4">
        <AreaTile icon={area.icon} />
        {/* `truncate` clips to the box: `py-1 -my-1` gives accents and descenders room without moving the row. */}
        <h1 className="-my-1 min-w-0 flex-1 truncate py-1 text-[30px] leading-9 font-normal tracking-tight text-foreground md:text-page md:leading-11">
          {area.label}
        </h1>
        <Button variant="outline" onClick={onEdit} data-edit-area-button>
          <Pencil className="md:hidden" />
          <span className="hidden md:inline">Edit area</span>
          <span className="sr-only md:hidden">Edit area</span>
        </Button>
      </div>
      {area.summary ? (
        <div className="flex flex-col gap-1" data-area-summary>
          <p className="text-lead text-foreground">{area.summary}</p>
          {area.summary_at ? (
            <p className="text-meta text-hint">
              Where it stands · written by {author}{" "}
              {shortTime(area.summary_at, me.workspace.timezone)}
            </p>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}

function Archived({ page }: { page: AreaPageData }) {
  const restore = useEditArea(page.area.id);
  return (
    <div
      role="status"
      data-area-archived
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-secondary px-5 py-3 text-body"
    >
      <span>This area is archived. It's out of the sidebar, and the crew isn't told about it.</span>
      <Button
        size="sm"
        variant="outline"
        disabled={restore.isPending}
        onClick={() => restore.mutate({ archived: false })}
      >
        Restore
      </Button>
      {restore.error ? (
        <span className="text-meta text-brick">{errorHint(restore.error)}</span>
      ) : null}
    </div>
  );
}

function Ask({ page }: { page: AreaPageData }) {
  const navigate = useNavigate();
  const { area } = page;
  return (
    <div data-area-ask>
      <StartComposer
        placeholder={`Ask about ${area.label.toLowerCase()}. Say what done looks like.`}
        label={`Ask about ${area.label}`}
        areaId={area.id}
        onStarted={(threadId) => navigate(`/chats/${threadId}`)}
      />
      <span className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-1 text-meta text-copy">
        <ChatIcon name={area.icon} size={14} />
        {area.label}
      </span>
    </div>
  );
}

/** The page's three lists, built once for both layouts. */
function listsOf(page: AreaPageData, me: Me) {
  const label = page.area.label.toLowerCase();
  const tasks =
    page.tasks.length > 0 ? (
      <TaskRows tasks={page.tasks} me={me} />
    ) : (
      <Empty>No open tasks here. Ask, and the crew picks it up.</Empty>
    );
  const chats =
    page.chats.length > 0 ? (
      <ChatRows chats={page.chats} me={me} />
    ) : (
      <Empty>No chats here yet. Ask about {label} to start one.</Empty>
    );
  const needs =
    page.needs.length > 0 ? (
      <Section title="Needs someone" count={page.needs.length} data-area-needs>
        <TaskRows tasks={page.needs} me={me} />
      </Section>
    ) : null;
  return { tasks, chats, needs };
}

/** A phone's: Needs someone, then Tasks and Chats as tabs (A1b). */
function PhoneLists({ page, me }: { page: AreaPageData; me: Me }) {
  const [tab, setTab] = useState<"tasks" | "chats">("tasks");
  const { tasks, chats, needs } = listsOf(page, me);
  return (
    <div className="flex flex-col gap-6 md:hidden">
      {needs}
      <Tabs value={tab} onValueChange={(v) => setTab(v as "tasks" | "chats")}>
        <TabsList aria-label="Tasks and chats">
          <TabsTrigger value="tasks">
            Tasks <span className="text-muted-foreground tabular">{page.task_counts.open}</span>
          </TabsTrigger>
          <TabsTrigger value="chats">
            Chats <span className="text-muted-foreground tabular">{page.chat_count}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === "tasks" ? tasks : chats}
      <TextLink href={`/${tab}?area=${page.area.slug}`} className="self-start text-meta">
        {tab === "tasks" ? "All in Tasks" : "All in Chats"}
      </TextLink>
    </div>
  );
}

/** A desktop's: Needs someone, Tasks and Chats, one under the other (A1). */
function DesktopLists({ page, me }: { page: AreaPageData; me: Me }) {
  const { tasks, chats, needs } = listsOf(page, me);
  return (
    <div className="hidden flex-col gap-8 md:flex">
      {needs}
      <Section
        title="Tasks"
        count={`${page.task_counts.open} open · ${page.task_counts.done} done`}
        aside={<TextLink href={`/tasks?area=${page.area.slug}`}>All in Tasks</TextLink>}
        data-area-tasks
      >
        {tasks}
      </Section>
      <Section
        title="Chats"
        count={page.chat_count}
        aside={<TextLink href={`/chats?area=${page.area.slug}`}>All in Chats</TextLink>}
        data-area-chats
      >
        {chats}
      </Section>
    </div>
  );
}

/** A folded fact: cut to its lines, and hidden past the facts a width shows. */
function foldedFact(i: number) {
  return cn(
    "line-clamp-2 md:line-clamp-3",
    i >= CARD_FOLD.phone.facts && "max-md:hidden",
    i >= CARD_FOLD.desktop.facts && "md:hidden",
  );
}

/**
 * Whether the folded facts hide anything at this width: a fact past the ones
 * shown, or one cut by its lines. Measured, so it holds for any words.
 */
function useClipped(open: boolean, facts: readonly string[]) {
  const ref = useRef<HTMLUListElement>(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const list = ref.current;
    if (!list || open) return;
    const check = () => setClipped([...list.children].some(isClipped));
    check();
    const observer = new ResizeObserver(check);
    observer.observe(list);
    return () => observer.disconnect();
  }, [open, facts]);
  return [ref, clipped] as const;
}

const isClipped = (el: Element) =>
  el.getClientRects().length === 0 || el.scrollHeight > el.clientHeight + 1;

/** Show all, or Show less once open. */
function ShowAll({
  count,
  open,
  onToggle,
}: {
  count: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      data-area-brief-more
      className="inline-flex items-center gap-1 self-start text-meta font-medium text-foreground"
    >
      {open ? "Show less" : `Show all ${count}`}
      <ChevronDown aria-hidden className={cn("size-3.5", open && "rotate-180")} />
    </button>
  );
}

/**
 * The card's facts, folded (spec §8.2): the first few, each cut to a few
 * lines, and Show all to open the rest in place.
 */
function BriefFacts({ facts, titles }: { facts: string[]; titles: ReadonlyMap<string, string> }) {
  const [open, setOpen] = useState(false);
  const [ref, clipped] = useClipped(open, facts);
  if (facts.length === 0) {
    return (
      <p className="text-body text-muted-foreground">Nothing yet. Add what the crew should know.</p>
    );
  }
  return (
    <>
      <ul ref={ref} className="flex flex-col gap-2 text-body text-foreground">
        {facts.map((fact, i) => (
          <li key={i} data-area-brief-fact className={open ? undefined : foldedFact(i)}>
            <MentionText text={fact} titles={titles} />
          </li>
        ))}
      </ul>
      {clipped ? (
        <ShowAll count={facts.length} open={open} onToggle={() => setOpen(!open)} />
      ) : null}
    </>
  );
}

/** What the crew knows (spec §8.2; A1, A1b): the brief's facts, folded, and Edit. */
function BriefCard({ page }: { page: AreaPageData }) {
  const { facts, titles } = cardBrief(page.brief);
  return (
    <section
      className="flex flex-col gap-3 rounded-xl bg-card px-5 py-4 ring-1 ring-border"
      data-area-brief
    >
      <div className="flex items-baseline justify-between">
        <Eyebrow>
          What the crew knows
          {facts.length > 0 ? (
            <span className="ml-2 font-normal tabular">{facts.length}</span>
          ) : null}
        </Eyebrow>
        <TextLink href={briefHref(page.area.slug)}>{facts.length > 0 ? "Edit" : "Add"}</TextLink>
      </div>
      <BriefFacts facts={facts} titles={titles} />
      <p className="border-t border-border pt-3 text-meta text-hint">
        The crew reads this whenever it works on the {page.area.label.toLowerCase()}, and keeps it
        up to date.
      </p>
    </section>
  );
}

function Side({ page, me }: { page: AreaPageData; me: Me }) {
  return (
    <>
      <BriefCard page={page} />
      <SideCard
        title="Docs"
        count={page.doc_count}
        href={`/docs?area=${page.area.slug}`}
        data-area-docs
      >
        <DocList page={page} me={me} />
      </SideCard>
      <SideCard
        title="Routines"
        count={page.routines.length}
        href={`/routines?for=all&area=${page.area.slug}`}
        data-area-routines
      >
        <RoutineList routines={page.routines} me={me} />
      </SideCard>
      <SideCard title="For this area" data-area-apps>
        <AreaApps
          apps={page.apps ?? []}
          areaId={page.area.id}
          archived={page.area.archived_at !== null}
        />
      </SideCard>
      {page.week ? <WeekLine week={page.week} slug={page.area.slug} /> : null}
    </>
  );
}

function Loading({ error }: { error: unknown }) {
  return (
    <main className="flex w-full max-w-6xl flex-col gap-5 px-4 pt-6 pb-16 sm:px-6 md:px-12 md:pt-9">
      {error ? (
        <p className="text-body text-muted-foreground">{errorHint(error)}</p>
      ) : (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Spinner className="size-5" />
        </div>
      )}
    </main>
  );
}

/** New task here and Move chats in, under the lists of a live Area. */
function PageActions({ onTask, onMoveIn }: { onTask: () => void; onMoveIn: () => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="ghost" onClick={onTask} data-area-new-task>
        <Plus />
        New task here
      </Button>
      <Button variant="ghost" onClick={onMoveIn} data-area-move-in>
        <ArrowDownToLine />
        Move chats and docs in
      </Button>
    </div>
  );
}

/** Move these in?, open when the URL says `?move=1` (after New area, or the crew's line). */
function useMoveInParam() {
  const [params, setParams] = useSearchParams();
  const open = params.get("move") === "1";
  const set = (next: boolean) =>
    setParams(
      (p) => {
        const q = new URLSearchParams(p);
        if (next) q.set("move", "1");
        else q.delete("move");
        return q;
      },
      { replace: true },
    );
  return [open, set] as const;
}

/** Edit area, New task here and Move these in?, each open or not. */
function useWindows() {
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [moving, setMoving] = useMoveInParam();
  return { editing, setEditing, creating, setCreating, moving, setMoving };
}

/** What a live Area has and an archived one doesn't: the ask, New task here and Move chats in. */
function LiveAsk({ page }: { page: AreaPageData }) {
  if (page.area.archived_at) return null;
  return <Ask page={page} />;
}

function LiveActions({ page, w }: { page: AreaPageData; w: ReturnType<typeof useWindows> }) {
  if (page.area.archived_at) return null;
  return <PageActions onTask={() => w.setCreating(true)} onMoveIn={() => w.setMoving(true)} />;
}

function Windows({
  data,
  me,
  w,
}: {
  data: AreaPageData;
  me: Me;
  w: ReturnType<typeof useWindows>;
}) {
  return (
    <>
      <EditAreaWindow
        area={data.area}
        moves={areaMoves(data.area, me)}
        open={w.editing}
        onOpenChange={w.setEditing}
      />
      <NewTaskWindow open={w.creating} onOpenChange={w.setCreating} areaId={data.area.id} />
      <MoveInWindow
        area={data.area}
        me={me}
        open={w.moving && !data.area.archived_at}
        onOpenChange={w.setMoving}
      />
    </>
  );
}

function Loaded({ data, me }: { data: AreaPageData; me: Me }) {
  const w = useWindows();
  return (
    <main
      data-area-page={data.area.slug}
      className="flex w-full max-w-6xl flex-col gap-6 px-4 pt-6 pb-16 sm:px-6 md:flex-row md:items-start md:gap-12 md:px-12 md:pt-9"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-6 md:gap-8">
        {data.area.archived_at ? <Archived page={data} /> : null}
        <Header page={data} me={me} onEdit={() => w.setEditing(true)} />
        <LiveAsk page={data} />
        <DesktopLists page={data} me={me} />
        <PhoneLists page={data} me={me} />
        <LiveActions page={data} w={w} />
      </div>
      <aside className="flex w-full flex-col gap-4 md:w-[320px] md:shrink-0">
        <Side page={data} me={me} />
      </aside>
      <Windows data={data} me={me} w={w} />
    </main>
  );
}

export function AreaPage({ slug, me }: { slug: string; me: Me }) {
  const page = useAreaPage(slug);
  return page.data ? <Loaded data={page.data} me={me} /> : <Loading error={page.error} />;
}
